import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BuildBuyInputs {
  nature?: string;
  nature_notes?: string;
  data_sensitivity?: string;
  data_notes?: string;
  urgency?: string;
  internal_capacity?: string;
  budget?: string;
  budget_notes?: string;
  regulatory_notes?: string;
  candidate_vendor?: string;
  free_notes?: string;
}

export type BuildBuyRecommendation =
  | "buy_saas"
  | "build_internal"
  | "outsource_dev"
  | "hybrid";

export interface BuildBuyResult {
  recommendation: BuildBuyRecommendation;
  confidence: number;
  rationale: string;
  alignment: string;
  risks: string[];
  conditions: string[];
  next_steps: string[];
  alternatives: Record<string, string>;
}

export interface BuildBuyEvaluationRow {
  id: string;
  project_id: string;
  organization_id: string | null;
  created_by: string | null;
  inputs: BuildBuyInputs;
  result: BuildBuyResult;
  protocol_snapshot: string | null;
  model_used: string | null;
  created_at: string;
}

export function useBuildBuyHistory(projectId: string | undefined) {
  return useQuery({
    queryKey: ["build_buy_evaluations", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("build_buy_evaluations" as any)
        .select("*")
        .eq("project_id", projectId!)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as unknown as BuildBuyEvaluationRow[];
    },
  });
}

export function useEvaluateBuildBuy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectId,
      inputs,
    }: {
      projectId: string;
      inputs: BuildBuyInputs;
    }) => {
      const { data, error } = await supabase.functions.invoke(
        "evaluate-build-buy",
        { body: { project_id: projectId, inputs } },
      );
      if (error) throw error;
      return data as BuildBuyEvaluationRow;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["build_buy_evaluations", vars.projectId] });
    },
  });
}

const DEFAULT_PROTOCOL_PLACEHOLDER = `Ejemplo:
- Si los datos contienen PII de clientes en LATAM, preferir Build interno o SaaS con datacenter regional.
- Tercerizar solo a proveedores con NDA y experiencia >3 años en nuestro sector.
- No comprar SaaS que duplique funcionalidad ya licenciada (revisar Microsoft 365 / Google Workspace primero).
- Para proyectos <50k USD y urgencia <3 meses, siempre Buy.
- Build interno requiere aprobación del CoE si involucra modelos generativos.`;

export function useBuildBuyProtocol(orgId: string | undefined) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["enterprise_settings_protocol", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("enterprise_settings")
        .select("build_buy_protocol")
        .eq("organization_id", orgId!)
        .maybeSingle();
      if (error) throw error;
      return (data?.build_buy_protocol as string | null) ?? "";
    },
  });

  const mutation = useMutation({
    mutationFn: async (protocol: string) => {
      const { error } = await supabase
        .from("enterprise_settings")
        .upsert(
          { organization_id: orgId!, build_buy_protocol: protocol },
          { onConflict: "organization_id" },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enterprise_settings_protocol", orgId] });
    },
  });

  return { ...query, save: mutation, placeholder: DEFAULT_PROTOCOL_PLACEHOLDER };
}
